import { memo, useMemo } from 'react'
import { ButtonBase, SxProps } from '@mui/material'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { Text } from '@/components/Data'
import { Box } from '@/components/Layout'
import { dateRangePickerMonthsClasses } from './DateRangePickerMonths.data'
import { DateRangePickerMonthsProps } from './DateRangePickerMonths.types'
import { dateRangePickerMonthsStyles as styles } from './DateRangePickerMonths.style'

function DateRangePickerMonths({
  date,
  disablePastDates,
  disableFutureDates,
  minDate,
  maxDate,
  onMonthSelect,
  wrapperProps = {},
  monthWrapperProps = {},
  buttonProps = {},
}: DateRangePickerMonthsProps) {
  const currentYear = date.year()
  const today = dayjs()

  // Get abbreviated month names
  const months = useMemo(() => dayjs.monthsShort(), [])

  // Calculate min month index
  const minMonth = useMemo(() => {
    let minMonthIdx = 0
    if (disablePastDates && currentYear === today.year()) minMonthIdx = today.month()

    if (minDate && dayjs(minDate).year() === currentYear) minMonthIdx = dayjs(minDate).month()

    return minMonthIdx
  }, [disablePastDates, minDate, currentYear, today])

  const maxMonth = useMemo(() => {
    let maxMonthIdx = 11

    if (disableFutureDates && currentYear === today.year()) maxMonthIdx = today.month()

    if (maxDate && dayjs(maxDate).year() === currentYear) maxMonthIdx = dayjs(maxDate).month()

    return maxMonthIdx
  }, [disableFutureDates, maxDate, currentYear, today])

  // props extraction
  const { sx: wrapperSx, ...otherWrapperProps } = wrapperProps
  const { sx: monthWrapperSx, ...otherMonthWrapperProps } = monthWrapperProps
  const { sx: buttonSx, className: buttonClassName = '', ...otherButtonProps } = buttonProps
  return (
    <Box sx={[styles.wrapper, wrapperSx] as SxProps} {...otherWrapperProps}>
      <Box sx={[styles.monthsWrapper, monthWrapperSx] as SxProps} {...otherMonthWrapperProps}>
        {months.map((month, monthIndex) => (
          <ButtonBase
            key={month}
            className={clsx({
              [dateRangePickerMonthsClasses.selected]: date.month() === monthIndex,
              [buttonClassName]: Boolean(buttonClassName),
            })}
            sx={[styles.button, buttonSx] as SxProps}
            onClick={() => onMonthSelect(monthIndex)}
            disabled={monthIndex < minMonth || monthIndex > maxMonth}
            {...otherButtonProps}>
            <Text sx={styles.text}>{month}</Text>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  )
}

export default memo(DateRangePickerMonths)
